import React, { useLayoutEffect, useState } from "react";
import LayoutFooter from "../inc/LayoutFooter";
import LayoutHeader from "../inc/LayoutHeader";
import { useNavigate } from "react-router-dom";
import {
  RollbackOutlined,
  DeleteOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import { Layout, Col, Row, Select, List, Space } from "antd";
import { Button, Card, Form, Spin, Avatar, Input } from "antd";
import {
  displayMessage,
  generateSlug,
  getAPI,
  postAPI,
} from "../../utils/common";
import { ERROR_MSG_TYPE, SUCCESS_MESSAGE } from "../../constants/messages";
import {
  ALL_BRAND,
  ALL_CATEGORY,
  CREATE_PRODUCT,
  IMAGE_BASE_URL,
  SUB_CATEGORY_CAT_ID,
} from "../../constants/api";
import ImageSelect from "../images/ImageSelect";
import JoditEditor from "jodit-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

function ProductAdd() {
  const { Content } = Layout;
  let history = useNavigate();
  let navigate = useNavigate();

  const [form] = Form.useForm();
  const [categoryList, setCategory] = useState([]);
  const [subCategoryList, setSubCategory] = useState([]);
  const [brandList, setBrand] = useState([]);
  const [loading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // For Image Selection
  const [selectedImages, setSelectedImages] = useState([]);
  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handleDialogOk = (selectedImages) => {
    if (selectedImages.length > 0) {
      const selectedImage = selectedImages.map((image) => image);
      setSelectedImages(selectedImage);
      form.setFieldsValue({ product_image: selectedImage.length });
    } else {
      displayMessage(ERROR_MSG_TYPE, "Image not selected");
    }
  };

  const removeSelectedImage = (filePathToRemove) => {
    const updatedSelectedFileValues = selectedImages.filter(
      (filePath) => filePath.filePath !== filePathToRemove
    );
    setSelectedImages(updatedSelectedFileValues);
    form.setFieldsValue({ image: updatedSelectedFileValues.length });
  };

  const generateSkuCode = () => {
    const newSkuCode = `SKU-${Math.random()
      .toString(36)
      .substr(2, 9)
      .toUpperCase()}`;
    form.setFieldsValue({ sku_code: newSkuCode });
  };

  const generateBarcodeCode = () => {
    const newBarcode = `BAR-${Math.random()
      .toString(36)
      .substr(2, 12)
      .toUpperCase()}`;
    form.setFieldsValue({ barcode: newBarcode });
  };

  useLayoutEffect(() => {
    getCategory();
    getBrand();
    generateSkuCode();
    generateBarcodeCode();
  }, []);

  const getCategory = () => {
    setIsLoading(true);
    const successFn = function (data) {
      if (data.status === true) {
        setIsLoading(false);
        setCategory(data.data.map((e) => ({ value: e.id, label: e.title })));
      } else {
        displayMessage(ERROR_MSG_TYPE, data.message);
        setCategory([]);
        setIsLoading(false);
      }
    };
    const errorFn = function () {
      setCategory([]);
      setIsLoading(false);
    };
    getAPI(ALL_CATEGORY, successFn, errorFn, {});
  };

  const getSubCategory = (id) => {
    setIsLoading(true);
    const successFn = function (data) {
      if (data.status === true) {
        setIsLoading(false);
        setSubCategory(data.data.map((e) => ({ value: e.id, label: e.name })));
      } else {
        displayMessage(ERROR_MSG_TYPE, data.message);
        setSubCategory([]);
        setIsLoading(false);
      }
    };
    const errorFn = function () {
      setSubCategory([]);
      setIsLoading(false);
    };
    postAPI(SUB_CATEGORY_CAT_ID, { id }, successFn, errorFn, {});
  };

  const getBrand = () => {
    setIsLoading(true);
    const successFn = function (data) {
      if (data.status === true) {
        setIsLoading(false);
        setBrand(data.data.map((e) => ({ value: e.id, label: e.title })));
      } else {
        displayMessage(ERROR_MSG_TYPE, data.message);
        setBrand([]);
        setIsLoading(false);
      }
    };
    const errorFn = function () {
      setBrand([]);
      setIsLoading(false);
    };
    getAPI(ALL_BRAND, successFn, errorFn, {});
  };

  // Handle title change and update slug
  const handleTitleChange = (event) => {
    const newTitle = event.target.value;
    form.setFieldsValue({ slug: generateSlug(newTitle) });
  };

  const createData = (values) => {
    setIsLoading(true);
    const jsonData = {
      title: values.title,
      sub_category: values.sub_category,
      brand: values.brand,
      sku_code: values.sku_code,
      barcode: values.barcode,
      avl_stock: values.avl_stock,
      price: values.price,
      mrp: values.mrp,
      tax: values.tax,
      min_cart: values.min_cart,
      short_description: values.short_description,
      description: values.description,
      meta_title_new: values.slug,
      meta_title: values.meta_title,
      meta_description: values.meta_description,
      meta_keyword: values.meta_keyword,
      tags: values.tags,
      rating: values.rating,
      image: Array.from(selectedImages).map((element) => element.id),
    };
    const successFn = function (data) {
      if (data.status === true) {
        setIsLoading(false);
        history("/product");
        displayMessage(SUCCESS_MESSAGE, data.message);
      } else {
        setIsLoading(false);
        displayMessage(ERROR_MSG_TYPE, data.message);
      }
    };
    const errorFn = function () {
      setIsLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to Save Data");
    };
    postAPI(CREATE_PRODUCT, jsonData, successFn, errorFn);
  };
  return (
    <>
      <Layout>
        <LayoutHeader />
        <Content>
          <Spin spinning={loading}>
            <Card
              title="Create Product"
              bordered={false}
              extra={
                <Button
                  style={{
                    marginTop: "5px",
                    marginBottom: "5px",
                  }}
                  onClick={() => navigate(-1)}
                  type="primary"
                  ghost
                >
                  <RollbackOutlined />
                </Button>
              }
            >
              <Form form={form} layout="vertical" onFinish={createData}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Form.Item
                      name="title"
                      label="Product Name"
                      rules={[
                        {
                          required: true,
                          message: "Product Name is required!",
                        },
                      ]}
                      onChange={handleTitleChange}
                    >
                      <Input type={"text"} placeholder="Product Name" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name="category"
                      label="Category"
                      rules={[
                        {
                          required: true,
                          message: "Category is required!",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        allowClear
                        placeholder="Select a Category"
                        optionFilterProp="label"
                        onChange={getSubCategory}
                        options={categoryList}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name="sub_category"
                      label="Sub Category"
                      rules={[
                        {
                          required: true,
                          message: "Sub Category is required!",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        allowClear
                        placeholder="Select a Sub Category"
                        optionFilterProp="label"
                        options={subCategoryList}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name="brand"
                      label="Brand"
                      rules={[
                        {
                          required: true,
                          message: "Brand is required!",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        allowClear
                        placeholder="Select a Brand"
                        optionFilterProp="label"
                        options={brandList}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name="sku_code"
                      label="SKU Code"
                      rules={[
                        {
                          required: true,
                          message: "SKU Code is required!",
                        },
                      ]}
                    >
                      <Input
                        type={"text"}
                        placeholder="SKU Code"
                        suffix={<RetweetOutlined onClick={generateSkuCode} />}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name="barcode"
                      label="Barcode"
                      rules={[
                        {
                          required: true,
                          message: "Barcode is required!",
                        },
                      ]}
                    >
                      <Input
                        type={"text"}
                        placeholder="Barcode"
                        suffix={
                          <RetweetOutlined onClick={generateBarcodeCode} />
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name="avl_stock"
                      label="Available Stock"
                      rules={[
                        {
                          required: true,
                          message: "Available Stock is required!",
                        },
                        {
                          pattern: /^[0-9]+$/,
                          message: "Available Stock must be a number",
                        },
                      ]}
                    >
                      <Input type="text" placeholder="Available Stock" />
                    </Form.Item>
                  </Col>

                  <Col span={4}>
                    <Form.Item
                      name="price"
                      label="Price"
                      rules={[
                        {
                          required: true,
                          message: "Price is required!",
                        },
                        {
                          pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
                          message: "Please enter a valid price (e.g., 100.99)",
                        },
                      ]}
                    >
                      <Input type="text" placeholder="Price" />
                    </Form.Item>
                  </Col>

                  <Col span={4}>
                    <Form.Item
                      name="mrp"
                      label="MRP"
                      rules={[
                        {
                          required: true,
                          message: "MRP is required!",
                        },
                        {
                          pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
                          message: "Please enter a valid MRP (e.g., 200.99)",
                        },
                      ]}
                    >
                      <Input type="text" placeholder="MRP" />
                    </Form.Item>
                  </Col>

                  <Col span={4}>
                    <Form.Item
                      name="tax"
                      label="Tax"
                      rules={[
                        {
                          required: true,
                          message: "Tax is required!",
                        },
                        {
                          pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
                          message:
                            "Please enter a valid tax value (e.g., 18.00)",
                        },
                      ]}
                    >
                      <Input type="text" placeholder="Tax" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name="tags"
                      label="Tags"
                      rules={[
                        {
                          required: true,
                          message: "Tags is required!",
                        },
                      ]}
                    >
                      <Input type={"text"} placeholder="Tags" />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Form.Item
                      name="min_cart"
                      label="Max Cart"
                      rules={[
                        {
                          required: true,
                          message: "Max Cart is required!",
                        },
                        {
                          pattern: /^[0-9]+$/,
                          message: "Max Cart must be a valid number",
                        },
                      ]}
                    >
                      <Input type="text" placeholder="Max Cart" />
                    </Form.Item>
                  </Col>

                  <Col span={2}>
                    <Form.Item
                      name="rating"
                      label="Rating"
                      rules={[
                        {
                          required: true,
                          message: "Rating is required!",
                        },
                      ]}
                    >
                      <Input type={"text"} placeholder="Rating" />
                    </Form.Item>
                  </Col>

                  <Col span={4}>
                    <Form.Item
                      name="slug"
                      label="Slug"
                      rules={[{ required: true, message: "Slug is required!" }]}
                    >
                      <Input disabled type={"text"} placeholder="Slug" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="short_description"
                      label="Short Description"
                      rules={[
                        {
                          required: true,
                          message: "Short Description is required!",
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Short Description"
                      />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      name="meta_title"
                      label="SEO Title (Title Tag)"
                      rules={[
                        { required: true, message: "SEO Title is required!" },
                        {
                          min: 50,
                          max: 60,
                          message:
                            "SEO Title must be between 50 and 60 characters.",
                        },
                      ]}
                    >
                      <Input placeholder="SEO Title (Title Tag)" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="meta_keyword"
                      label="SEO Keyword"
                      rules={[
                        { required: true, message: "SEO Keyword is required!" },
                        {
                          min: 1,
                          max: 50,
                          message:
                            "SEO Keyword should be between 1 and 50 characters.",
                        },
                      ]}
                    >
                      <Input placeholder="SEO Keyword" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="meta_description"
                      label="SEO Meta Description"
                      rules={[
                        {
                          required: true,
                          message: "SEO Meta Description is required!",
                        },
                        {
                          min: 50,
                          max: 160,
                          message:
                            "SEO Meta Description must be between 50 and 160 characters.",
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="SEO Meta Description"
                      />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      type="file"
                      name="product_image"
                      label="Images"
                      rules={[
                        { required: true, message: "Images is required!" },
                      ]}
                    >
                      <Input
                        type="text"
                        readOnly
                        onClick={openDialog}
                        value={
                          selectedImages.length === 0
                            ? "Click to select image"
                            : selectedImages.length
                        }
                        name="product_image"
                        placeholder="Click to select image"
                      />

                      {selectedImages.length !== 0 ? (
                        <List
                          grid={{
                            gutter: 16,
                            column: 3,
                          }}
                          dataSource={selectedImages}
                          renderItem={(element) => (
                            <Space>
                              <Avatar
                                size={{
                                  xs: 24,
                                  sm: 32,
                                  md: 40,
                                  lg: 64,
                                  xl: 80,
                                  xxl: 100,
                                }}
                                src={IMAGE_BASE_URL + element.filePath}
                                shape="square"
                              />
                              <Avatar
                                shape="circle"
                                size="small"
                                icon={
                                  <DeleteOutlined
                                    onClick={() =>
                                      removeSelectedImage(element.filePath)
                                    }
                                  />
                                }
                              />
                            </Space>
                          )}
                        />
                      ) : null}
                    </Form.Item>
                  </Col>
                  <Col span={18}>
                    <Form.Item
                      name="description"
                      label="Description"
                      rules={[
                        {
                          required: true,
                          message: "Description is required!",
                        },
                      ]}
                    >
                      <JoditEditor placeholder="Description" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row spceber>
                  <Col span={24}>
                    <Form.Item
                      style={{ float: "right" }}
                      label=""
                      colon={false}
                    >
                      <Button loading={loading} block htmlType="submit">
                        Submit
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Spin>
        </Content>
        <LayoutFooter />
        <ImageSelect
          isOpen={isDialogOpen}
          onClose={closeDialog}
          onOk={handleDialogOk}
          fromMager={false}
        />
      </Layout>
    </>
  );
}
export default ProductAdd;
